/**
 * Validation shared by the forms (instant feedback) and the API routes
 * (the source of truth). Every function returns a map of field errors.
 */

import {
    COMPANY_SIZES,
    CONTACT_TOPICS,
    CURRENT_TOOLS,
    INDUSTRIES,
    MEETING_TIMES,
    PLAN_INTERESTS,
    PLAN_OPTIONS
} from '@/constants/forms'

export type FieldErrors<T extends string = string> = Partial<Record<T, string>>

const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/

export const isEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim()) && value.trim().length <= 254

export const clean = (value: unknown, max = 500): string => {
    return typeof value === 'string' ? value.trim().slice(0, max) : ''
}

export const cleanList = (value: unknown, allowed: Array<string>): Array<string> => {
    if (!Array.isArray(value)) return []

    return value.filter((item): item is string => typeof item === 'string' && allowed.includes(item)).slice(0, allowed.length)
}

export type ContactInput = {
    name: string
    email: string
    company: string
    topic: string
    message: string
}

export const validateContact = (input: ContactInput): FieldErrors<keyof ContactInput> => {
    const errors: FieldErrors<keyof ContactInput> = {}

    if (input.name.length < 2) errors.name = 'Please enter your name.'
    if (!isEmail(input.email)) errors.email = 'Please enter a valid email address.'
    if (!CONTACT_TOPICS.includes(input.topic)) errors.topic = 'Please choose a topic.'
    if (input.message.length < 10) errors.message = 'Please write a few more words so we can help.'
    if (input.message.length > 4000) errors.message = 'Please keep your message under 4,000 characters.'

    return errors
}

export type DemoInput = {
    firstName: string
    lastName: string
    email: string
    jobTitle: string
    company: string
    companySize: string
    industry: string
    tools: Array<string>
    goals: string
    planInterest: string
    preferredTime: string
}

export type DemoField = keyof DemoInput

export const DEMO_STEPS: Array<Array<DemoField>> = [
    ['firstName', 'lastName', 'email', 'jobTitle'],
    ['company', 'companySize', 'industry', 'tools'],
    ['goals', 'planInterest', 'preferredTime']
]

export const validateDemo = (input: DemoInput): FieldErrors<DemoField> => {
    const errors: FieldErrors<DemoField> = {}

    if (input.firstName.length < 1) errors.firstName = 'Please enter your first name.'
    if (input.lastName.length < 1) errors.lastName = 'Please enter your last name.'
    if (!isEmail(input.email)) errors.email = 'Please enter a valid work email.'
    if (input.jobTitle.length < 2) errors.jobTitle = 'Please enter your role.'
    if (input.company.length < 2) errors.company = 'Please enter your company name.'
    if (!COMPANY_SIZES.includes(input.companySize)) errors.companySize = 'Please choose your company size.'
    if (input.industry && !INDUSTRIES.includes(input.industry)) errors.industry = 'Please choose an industry from the list.'
    if (input.tools.some(tool => !CURRENT_TOOLS.includes(tool))) errors.tools = 'Please choose tools from the list.'
    if (input.goals.length > 2000) errors.goals = 'Please keep this under 2,000 characters.'
    if (input.planInterest && !PLAN_INTERESTS.includes(input.planInterest)) errors.planInterest = 'Please choose a plan from the list.'
    if (input.preferredTime && !MEETING_TIMES.includes(input.preferredTime)) errors.preferredTime = 'Please choose a time from the list.'

    return errors
}

export type SignupInput = {
    name: string
    email: string
    company: string
    password: string
    plan: string
    agree: boolean
}

export const validateSignup = (input: SignupInput): FieldErrors<keyof SignupInput> => {
    const errors: FieldErrors<keyof SignupInput> = {}

    if (input.name.length < 2) errors.name = 'Please enter your full name.'
    if (!isEmail(input.email)) errors.email = 'Please enter a valid work email.'
    if (input.password.length < 8) errors.password = 'Use at least 8 characters.'
    if (input.password.length > 128) errors.password = 'Use 128 characters or fewer.'
    if (input.plan && !PLAN_OPTIONS.some(option => option.value === input.plan)) errors.plan = 'Please choose a plan.'
    if (!input.agree) errors.agree = 'Please accept the terms to continue.'

    return errors
}

export type LoginInput = {
    email: string
    password: string
}

export const validateLogin = (input: LoginInput): FieldErrors<keyof LoginInput> => {
    const errors: FieldErrors<keyof LoginInput> = {}

    if (!isEmail(input.email)) errors.email = 'Please enter the email you signed up with.'
    if (!input.password) errors.password = 'Please enter your password.'

    return errors
}
